import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";

// Same visual language as the dashboard's date-range picker
// (components/SidebarLayout.jsx's MiniCalendar) -- navy accent (#2B3674),
// same grid/typography, same "Quick select" side panel pattern -- but this
// one picks a single exact instant (date *and* time), not a range, since
// that's what scheduled_for/deprecation_at/expires_at actually need.
// Deliberately a separate component rather than extending MiniCalendar
// itself: that component is range-only and used elsewhere (dashboard
// filtering), and bolting single-date+time mode onto it would risk that
// unrelated use case.

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MINUTE_STEP = 5;

const isSameDay = (a, b) =>
  Boolean(a) &&
  Boolean(b) &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfDay = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

const to12Hour = (hour24) => {
  const h = hour24 % 12;
  return h === 0 ? 12 : h;
};

const combineDateAndTime = (day, hour12, minute, ampm) => {
  const d = new Date(day);
  let hour24 = hour12 % 12;
  if (ampm === "PM") hour24 += 12;
  d.setHours(hour24, minute, 0, 0);
  return d;
};

const formatDisplay = (date) =>
  date
    ? date.toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "";

const FUTURE_PRESETS = [
  { label: "In 1 hour", getDate: () => new Date(Date.now() + 60 * 60 * 1000) },
  {
    label: "Tomorrow, 9 AM",
    getDate: () => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    },
  },
  {
    label: "In 3 days",
    getDate: () => {
      const d = new Date();
      d.setDate(d.getDate() + 3);
      d.setHours(9, 0, 0, 0);
      return d;
    },
  },
  {
    label: "In 1 week",
    getDate: () => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      d.setHours(9, 0, 0, 0);
      return d;
    },
  },
];

const NotificationDateTimePicker = ({
  selected,
  onChange,
  minDate = null,
  placeholder = "Select date and time",
  hasError = false,
  isClearable = false,
}) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pickerView, setPickerView] = useState("days"); // "days" | "months"
  const [pickerYear, setPickerYear] = useState((selected || new Date()).getFullYear());
  const [viewYear, setViewYear] = useState((selected || minDate || new Date()).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected || minDate || new Date()).getMonth());
  const [hour, setHour] = useState(selected ? to12Hour(selected.getHours()) : 9);
  const [minute, setMinute] = useState(selected ? selected.getMinutes() - (selected.getMinutes() % MINUTE_STEP) : 0);
  const [ampm, setAmpm] = useState(selected && selected.getHours() >= 12 ? "PM" : "AM");

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // hour/minute/ampm/viewYear/viewMonth/pickerYear are local copies of what
  // `selected` implies, not derived from it every render -- that's what
  // lets the calendar grid stay put while browsing months without a day
  // picked yet. But it also means they go stale if `selected` changes while
  // this component stays mounted without the popover re-opening -- exactly
  // what happens when the drawer's Local/UTC toggle swaps `selected` for a
  // shifted "display" Date (see CreateNotificationDrawer's
  // toUtcDisplayDate/fromUtcDisplayDate). Resyncing on every `selected`
  // change would fight the user while they're browsing months (a re-render
  // from something unrelated elsewhere in the drawer would snap the grid
  // back to the selected month), so resync only at the moment the popover
  // opens instead -- exactly when a stale value would otherwise be shown.
  useEffect(() => {
    if (!open) return;
    const base = selected || minDate || new Date();
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
    setPickerYear(base.getFullYear());
    setPickerView("days");
    if (selected) {
      setHour(to12Hour(selected.getHours()));
      setMinute(selected.getMinutes() - (selected.getMinutes() % MINUTE_STEP));
      setAmpm(selected.getHours() >= 12 ? "PM" : "AM");
    }
  }, [open]);

  const minDay = useMemo(() => (minDate ? startOfDay(minDate) : null), [minDate]);

  const isDayDisabled = (day) => (minDay ? startOfDay(day) < minDay : false);

  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

  const applyDate = (date) => onChange(date);

  const handleDayClick = (day) => {
    if (!day || isDayDisabled(day)) return;
    applyDate(combineDateAndTime(day, hour, minute, ampm));
  };

  const handleTimeChange = (nextHour, nextMinute, nextAmpm) => {
    setHour(nextHour);
    setMinute(nextMinute);
    setAmpm(nextAmpm);
    if (selected) applyDate(combineDateAndTime(selected, nextHour, nextMinute, nextAmpm));
  };

  const applyPreset = (getDate) => {
    const date = getDate();
    setViewYear(date.getFullYear());
    setViewMonth(date.getMonth());
    setHour(to12Hour(date.getHours()));
    setMinute(date.getMinutes());
    setAmpm(date.getHours() >= 12 ? "PM" : "AM");
    applyDate(date);
    setOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const selectMonth = (monthIdx) => {
    setViewMonth(monthIdx);
    setViewYear(pickerYear);
    setPickerView("days");
  };

  const dayClass = (day) => {
    if (!day) return "invisible";
    if (isDayDisabled(day)) return "text-gray-200 cursor-default";
    if (isSameDay(day, selected)) return "bg-[#2B3674] text-white font-semibold rounded-md cursor-pointer";
    return "text-gray-700 hover:bg-[#EEF0FB] hover:text-[#2B3674] cursor-pointer rounded-md";
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between rounded-xl border bg-gray-50 px-4 py-2.5 text-left text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/35 focus:border-blue-500 ${
          hasError ? "border-red-400" : "border-gray-200"
        }`}
      >
        <span className={selected ? "text-gray-800" : "text-gray-400"}>
          {selected ? formatDisplay(selected) : placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {isClearable && selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
            >
              <X size={14} />
            </span>
          )}
          <Calendar size={16} className="text-gray-400" />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 flex overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
          <div className="w-[232px] flex-shrink-0 p-3">
            {pickerView === "days" ? (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={prevMonth}
                    className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPickerYear(viewYear);
                      setPickerView("months");
                    }}
                    className="rounded-md px-2 py-1 text-xs font-semibold text-gray-700 transition hover:bg-[#EEF0FB] hover:text-[#2B3674]"
                  >
                    {MONTHS[viewMonth]} {viewYear}
                  </button>
                  <button
                    type="button"
                    onClick={nextMonth}
                    className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="mb-1 grid grid-cols-7">
                  {DAYS.map((d) => (
                    <div
                      key={d}
                      className="flex h-6 w-8 items-center justify-center text-[10px] font-semibold text-gray-400"
                    >
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7">
                  {cells.map((day, i) => (
                    <div
                      key={i}
                      className={`flex h-7 w-8 items-center justify-center text-xs transition-colors ${dayClass(day)}`}
                      onClick={() => handleDayClick(day)}
                    >
                      {day ? day.getDate() : ""}
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex items-center justify-center gap-1.5 border-t border-gray-100 pt-3">
                  <select
                    value={hour}
                    onChange={(e) => handleTimeChange(Number(e.target.value), minute, ampm)}
                    className="rounded-md border border-gray-200 px-1.5 py-1 text-xs text-gray-700"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-400">:</span>
                  <select
                    value={minute}
                    onChange={(e) => handleTimeChange(hour, Number(e.target.value), ampm)}
                    className="rounded-md border border-gray-200 px-1.5 py-1 text-xs text-gray-700"
                  >
                    {Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP).map((m) => (
                      <option key={m} value={m}>
                        {String(m).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                  <div className="ml-1 flex rounded-full border border-gray-200 p-0.5 text-xs">
                    {["AM", "PM"].map((period) => (
                      <button
                        key={period}
                        type="button"
                        onClick={() => handleTimeChange(hour, minute, period)}
                        className={`rounded-full px-2 py-0.5 font-medium transition-colors ${
                          ampm === period ? "bg-[#2B3674] text-white" : "text-gray-500"
                        }`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                </div>

                {isClearable && selected && (
                  <button
                    type="button"
                    onClick={() => onChange(null)}
                    className="mt-2 w-full rounded-full border border-gray-200 py-1 text-center text-[10px] text-gray-400 transition hover:border-red-300 hover:text-red-500"
                  >
                    Clear selection
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setPickerYear((y) => y - 1)}
                    className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <span className="text-xs font-semibold text-gray-700">{pickerYear}</span>
                  <button
                    type="button"
                    onClick={() => setPickerYear((y) => y + 1)}
                    className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {MONTHS.map((m, i) => {
                    const isActive = i === viewMonth && pickerYear === viewYear;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => selectMonth(i)}
                        className={`rounded-lg py-1.5 text-xs font-medium transition ${
                          isActive ? "bg-[#2B3674] text-white" : "text-gray-600 hover:bg-[#EEF0FB] hover:text-[#2B3674]"
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setPickerView("days")}
                  className="mt-3 w-full text-center text-[10px] text-gray-400 transition hover:text-gray-600"
                >
                  ← Back to calendar
                </button>
              </>
            )}
          </div>

          <div className="flex w-[130px] flex-shrink-0 flex-col justify-center gap-1.5 border-l border-gray-100 px-3 py-3">
            <p className="mb-1 text-[9px] font-semibold uppercase tracking-widest text-gray-400">Quick select</p>
            {FUTURE_PRESETS.map(({ label, getDate }) => (
              <button
                key={label}
                type="button"
                onClick={() => applyPreset(getDate)}
                className="w-full rounded-full border border-gray-200 px-2.5 py-1.5 text-center text-[11px] font-medium text-gray-500 transition hover:border-[#2B3674] hover:bg-[#2B3674] hover:text-white"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDateTimePicker;
